import { useEffect, useState } from "react";
import { api, getToken, API } from "../../lib/api";
import { Download, Search, Filter } from "lucide-react";

const STAGES = [
  { value: "", label: "All stages" },
  { value: "partial_email", label: "Email only - incomplete (partial)" },
  { value: "report_unlock", label: "Report unlock (name check gate)" },
  { value: "name_check", label: "Name check (PDF download)" },
  { value: "lead_captured", label: "Lead captured (newsletter / subscription)" },
  { value: "specialist_enquiry", label: "Specialist enquiry submitted" },
  { value: "checkout_started", label: "Checkout started" },
  { value: "payment_completed", label: "Payment completed" },
];

// Friendly labels for the raw `source` values stored on each lead.
const SOURCE_LABELS = {
  check_partial_email: "Email only - incomplete",
  check_report_unlock: "Report unlock",
};

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");

  // Reload whenever the stage filter changes - server-side filtering
  useEffect(() => {
    setLoading(true);
    const endpoint = stage ? `/admin/leads/by-stage?stage=${stage}` : "/admin/leads";
    api.get(endpoint)
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, [stage]);

  const filtered = items.filter((i) =>
    !q ||
    (i.email || "").toLowerCase().includes(q.toLowerCase()) ||
    (i.business_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (i.source || "").toLowerCase().includes(q.toLowerCase()) ||
    (i.name || "").toLowerCase().includes(q.toLowerCase()) ||
    (i.phone || "").toLowerCase().includes(q.toLowerCase()) ||
    (i.industry_label || i.industry || "").toLowerCase().includes(q.toLowerCase()) ||
    (i.postcode || "").toLowerCase().includes(q.toLowerCase())
  );

  const exportCsv = async () => {
    const token = getToken();
    const res = await fetch(`${API}/admin/leads/export`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bnp_leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-leads-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="bnp-chip bnp-chip-light">Leads</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-2 font-semibold tracking-tight">Captured leads</h1>
          <p className="text-sm text-[#447694] mt-1">
            {items.length} {stage ? `at "${STAGES.find((s) => s.value === stage)?.label}"` : "total"}
            {" · "}newsletter signups, report saves, partnership enquiries.
          </p>
        </div>
        <button onClick={exportCsv} className="btn-primary text-sm" data-testid="admin-leads-export-button">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#447694]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, name, phone, source, business name"
            className="bnp-input pl-10"
            data-testid="admin-leads-search-input"
          />
        </div>
        <div className="relative">
          <Filter size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#447694] pointer-events-none" />
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="bnp-input pl-10 pr-8 appearance-none cursor-pointer min-w-[260px]"
            data-testid="admin-leads-stage-filter"
          >
            {STAGES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bnp-card mt-5 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#447694] text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#447694] text-sm">
            No leads found{stage && ` at the "${STAGES.find((s) => s.value === stage)?.label}" stage`}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-[#f7f7f7] text-[#447694] text-left">
                <tr>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Phone</th>
                  <th className="px-5 py-3 font-medium">Source</th>
                  <th className="px-5 py-3 font-medium">Business name</th>
                  <th className="px-5 py-3 font-medium">Industry</th>
                  <th className="px-5 py-3 font-medium">Postcode</th>
                  <th className="px-5 py-3 font-medium">Notes</th>
                  <th className="px-5 py-3 font-medium">Captured</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-t border-[#9ac7f7]/20">
                    <td className="px-5 py-3 text-[#04273d]">{l.email}</td>
                    <td className="px-5 py-3 text-[#447694]">{l.name || "\u2014"}</td>
                    <td className="px-5 py-3 text-[#447694] font-mono-brand text-xs" data-testid={`admin-lead-phone-${l.id}`}>
                      {l.phone ? <a href={`tel:${l.phone}`} className="text-[#04273d] hover:text-[#0d9b80] underline underline-offset-2 decoration-[#9ac7f7]/60">{l.phone}</a> : "\u2014"}
                    </td>
                    <td className="px-5 py-3">
                      <span className="bnp-chip bnp-chip-light text-[10px]">{SOURCE_LABELS[l.source] || l.source || "\u2014"}</span>
                    </td>
                    <td className="px-5 py-3 text-[#447694]">{l.business_name || "\u2014"}</td>
                    <td className="px-5 py-3 text-[#447694]">{l.industry_label || l.industry || "\u2014"}</td>
                    <td className="px-5 py-3 text-[#447694] font-mono-brand text-xs">{l.postcode || "\u2014"}</td>
                    <td className="px-5 py-3 text-[#447694] max-w-xs truncate">{l.notes || "\u2014"}</td>
                    <td className="px-5 py-3 text-[#447694] font-mono-brand text-xs">
                      {l.created_at ? new Date(l.created_at).toLocaleString("en-GB") : "\u2014"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
